/**
 * EFT Activity Menu — fokus pada analitik EFT (bukan pengulangan tabel dashboard).
 *
 * Isi:
 *  - Tombol "+ Tambah Aktivitas EFT"
 *  - Top Performer (EFT closed + siswa TeachCast) — Admin/Manager/TL
 *  - Tabel "EFT didekati vs Active EFT School" per sales
 *  - Tabel detail meeting EFT per sales (lengkap)
 *
 * Staging EFT berbeda dari status TeachCast:
 *   Approaching → Follow Up → Meeting → Agreement Drafting → Active EFT School
 */
import { useEffect, useState } from "react";
import { Plus, RefreshCw, Zap, X, AlertTriangle, CheckCircle2, Trophy, Target } from "lucide-react";
import axios from "axios";
import { API } from "../../lib/api";
import { fmtN } from "../../lib/format";
import { useDrilldown } from "./DrilldownContext";

export default function EFTView({ currentUser }) {
  const [data, setData] = useState({ per_sales: [], meetings_detail: [], top_performers: [], stagings: [] });
  const [dropdowns, setDropdowns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [toast, setToast] = useState(null);
  const { openDrilldown } = useDrilldown();

  const load = () => {
    setLoading(true);
    axios.get(`${API}/eft/analytics`)
      .then((r) => setData(r.data || { per_sales: [], meetings_detail: [], top_performers: [], stagings: [] }))
      .catch(() => setToast("❌ Gagal load data EFT"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/eft/dropdowns`).then((r) => setDropdowns(r.data)).catch(() => {});
    load();
  }, []);

  return (
    <div data-testid="eft-view">
      <div className="section-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Zap size={16} color="var(--warn)" /> EFT Activity Center
        <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 10px" }} onClick={load} data-testid="eft-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
        <button className="btn btn-primary" style={{ background: "var(--warn)" }}
          onClick={() => setShowAdd(true)} data-testid="eft-add-btn">
          <Plus size={14} /> Tambah Aktivitas EFT
        </button>
      </div>

      {toast && <div className="alert" style={{ margin: "8px 0",
        background: toast.startsWith("✅") ? "rgba(39,174,96,.1)" : "rgba(231,76,60,.1)",
        color: toast.startsWith("✅") ? "var(--good)" : "var(--bad)" }}>{toast}</div>}

      {/* EFT Didekati vs Active EFT School per sales */}
      <div className="card card-3d" style={{ marginTop: 12 }} data-testid="eft-per-sales">
        <div className="card-h"><Target size={14} color="var(--accent)" />
          <strong style={{ marginLeft: 8 }}>EFT Didekati vs Jadi Active EFT School · per Sales</strong>
        </div>
        <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
          {loading ? <div className="empty" style={{ padding: 24 }}>Memuat…</div> :
            data.per_sales.length === 0 ? <div className="empty" style={{ padding: 24 }}>Belum ada aktivitas EFT.</div> :
            <table className="data-table" style={{ width: "100%" }} data-testid="eft-per-sales-table">
              <thead><tr>
                <th>Sales</th><th>Jumlah Meeting EFT</th><th>EFT Didekati (sekolah)</th>
                <th>Active EFT School</th><th>Konversi %</th>
              </tr></thead>
              <tbody>
                {data.per_sales.map((r) => (
                  <tr key={r.nama_sales} data-testid={`eft-ps-${r.nama_sales}`} style={{ cursor: "pointer" }}
                    onClick={() => openDrilldown({
                      title: `Aktivitas EFT · ${r.nama_sales}`,
                      staticRows: (data.meetings_detail || []).filter((m) => m.nama_sales === r.nama_sales),
                      rowType: "activities",
                    })}>
                    <td style={{ fontWeight: 600, color: "var(--navy)" }}>{r.nama_sales}</td>
                    <td style={{ color: r.meetings > 0 ? "var(--warn)" : "var(--muted)", fontWeight: 700 }}>{fmtN(r.meetings)}</td>
                    <td>{fmtN(r.approached)}</td>
                    <td style={{ fontWeight: 700, color: "var(--good)" }}>{fmtN(r.active_eft_schools)}</td>
                    <td style={{ fontWeight: 600 }}>{r.conversion_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>}
        </div>
      </div>

      {showAdd && dropdowns && (
        <AddEFTActivityModal
          dropdowns={dropdowns}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); setToast("✅ Aktivitas EFT tersimpan"); setTimeout(() => setToast(null), 3500); load(); }}
        />
      )}
    </div>
  );
}

/** Top Performer — dipindahkan ke Dashboard (dipakai di Dashboard.jsx). */
export function TopPerformersPanel() {
  const [top, setTop] = useState([]);
  useEffect(() => {
    axios.get(`${API}/eft/analytics`).then((r) => setTop(r.data?.top_performers || [])).catch(() => setTop([]));
  }, []);
  return (
    <div className="card card-3d" style={{ marginTop: 18 }} data-testid="dash-top-performers">
      <div className="card-h"><Trophy size={14} color="#F39C12" />
        <strong style={{ marginLeft: 8 }}>Top Performer</strong>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--muted)" }}>
          EFT closed terbanyak + siswa TeachCast terbanyak
        </span>
      </div>
      <div className="card-b" style={{ padding: 0, overflowX: "auto" }}>
        {top.length === 0 ? <div className="empty" style={{ padding: 24 }}>Belum ada data.</div> :
          <table className="data-table" style={{ width: "100%" }}>
            <thead><tr><th>#</th><th>Sales</th><th>EFT Closed (Active School)</th><th>Siswa TeachCast</th></tr></thead>
            <tbody>
              {top.map((p, i) => (
                <tr key={p.nama_sales} data-testid={`dash-top-${i}`}>
                  <td style={{ fontWeight: 700, color: i === 0 ? "#F39C12" : "var(--muted)" }}>{i + 1}</td>
                  <td style={{ fontWeight: 600, color: "var(--navy)" }}>
                    {i === 0 && <Trophy size={12} color="#F39C12" style={{ marginRight: 4, verticalAlign: "-2px" }} />}
                    {p.nama_sales}
                  </td>
                  <td style={{ fontWeight: 700, color: "var(--good)" }}>{fmtN(p.eft_closed)}</td>
                  <td style={{ fontWeight: 700, color: "var(--accent)" }}>{fmtN(p.teachcast_students)}</td>
                </tr>
              ))}
            </tbody>
          </table>}
      </div>
    </div>
  );
}

function EFTFormField({ label, req, children, span }) {
  return (
    <div className="field" style={span ? { gridColumn: `1 / ${span + 1}` } : {}}>
      <label style={{ fontSize: 11, fontWeight: 600 }}>
        {label} {req && <span style={{ color: "var(--bad)" }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function AddEFTActivityModal({ dropdowns, onClose, onSuccess }) {
  const nowLocal = new Date();
  const isoLocal = new Date(nowLocal.getTime() - nowLocal.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  const [form, setForm] = useState({
    nama_sales: "", status_sekolah: "", eft_staging: "", nama_sekolah: "", npsn: "",
    alamat: "", nama_yayasan: "", tujuan_sales: "EFT Purposed", tujuan_sales_other: "",
    kegiatan: "", kegiatan_lainnya: "",
    dilakukan_via: "", bertemu_dengan: "", jabatan: "", jabatan_lainnya: "",
    tanggal_visit: isoLocal, produk_menarik: "",
    demo_speakeng: "", respon_demo: "", respon_sekolah: "", detail_issue: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const requiredMissing = () => {
    const req = ["nama_sales", "status_sekolah", "eft_staging", "npsn", "alamat", "kegiatan",
      "dilakukan_via", "bertemu_dengan", "jabatan", "tanggal_visit",
      "produk_menarik", "demo_speakeng", "respon_sekolah", "detail_issue", "tujuan_sales"];
    return req.filter((k) => !form[k] || !String(form[k]).trim());
  };

  const submit = async () => {
    const missing = requiredMissing();
    if (missing.length > 0) { setErr("Field wajib belum diisi: " + missing.join(", ")); return; }
    if (form.kegiatan === "Lainnya" && !form.kegiatan_lainnya.trim()) { setErr("Wajib isi deskripsi kegiatan lainnya"); return; }
    if (form.jabatan === "Lainnya" && !form.jabatan_lainnya.trim()) { setErr("Wajib isi jabatan lainnya"); return; }
    if (form.tujuan_sales === "Lainnya" && !form.tujuan_sales_other.trim()) { setErr("Wajib isi tujuan sales lainnya"); return; }
    setBusy(true); setErr(null);
    try {
      await axios.post(`${API}/eft/activities`, form);
      onSuccess?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Gagal simpan aktivitas");
    } finally { setBusy(false); }
  };

  const Field = EFTFormField;
  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="eft-add-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(8,15,40,.7)", zIndex: 1100,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }}>
      <div className="card card-3d" style={{ width: "min(900px, 100%)", maxHeight: "92vh",
        display: "flex", flexDirection: "column", background: "var(--surface)" }}>
        <div className="card-h" style={{ padding: "14px 20px" }}>
          <Zap size={16} color="var(--warn)" />
          <strong style={{ marginLeft: 8 }}>Tambah Aktivitas EFT</strong>
          <button className="btn btn-ghost" style={{ marginLeft: "auto", padding: "4px 8px" }} onClick={onClose} data-testid="eft-add-close"><X size={14} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Nama Sales" req>
              <select className="select-input" value={form.nama_sales} onChange={set("nama_sales")} data-testid="eft-fm-sales">
                <option value="">— Pilih —</option>
                {dropdowns.nama_sales.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Status Sekolah yang di-approach" req>
              <select className="select-input" value={form.status_sekolah} onChange={set("status_sekolah")} data-testid="eft-fm-status">
                <option value="">— Pilih —</option>
                {dropdowns.status_sekolah.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Staging EFT" req>
              <select className="select-input" value={form.eft_staging} onChange={set("eft_staging")} data-testid="eft-fm-staging">
                <option value="">— Pilih —</option>
                {(dropdowns.eft_staging || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Nama Sekolah / Yayasan (opsional)">
              <input className="select-input" value={form.nama_sekolah} onChange={set("nama_sekolah")} data-testid="eft-fm-sekolah" />
            </Field>
            <Field label="NPSN" req>
              <input className="select-input" value={form.npsn} onChange={set("npsn")} placeholder="Angka NPSN sekolah" inputMode="numeric" data-testid="eft-fm-npsn" />
            </Field>
            <Field label="Alamat Sekolah / Yayasan" req>
              <input className="select-input" value={form.alamat} onChange={set("alamat")} data-testid="eft-fm-alamat" />
            </Field>
            <Field label="Nama Yayasan (opsional)">
              <input className="select-input" value={form.nama_yayasan} onChange={set("nama_yayasan")} data-testid="eft-fm-yayasan" />
            </Field>
            <Field label="Tanggal & Jam Visit" req>
              <input type="datetime-local" className="select-input" value={form.tanggal_visit} onChange={set("tanggal_visit")} data-testid="eft-fm-tanggal" />
            </Field>
            <Field label="Tujuan Sales" req>
              <select className="select-input" value={form.tujuan_sales} onChange={set("tujuan_sales")} data-testid="eft-fm-tujuan">
                {dropdowns.tujuan_sales.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            {form.tujuan_sales === "Lainnya" && (
              <Field label="Tuliskan Tujuan Sales Lainnya" req>
                <input className="select-input" value={form.tujuan_sales_other} onChange={set("tujuan_sales_other")} data-testid="eft-fm-tujuan-other" />
              </Field>
            )}
            <Field label="Kegiatan yang dilakukan" req span={2}>
              <select className="select-input" value={form.kegiatan} onChange={set("kegiatan")} data-testid="eft-fm-kegiatan">
                <option value="">— Pilih —</option>
                {dropdowns.kegiatan.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </Field>
            {form.kegiatan === "Lainnya" && (
              <Field label="Deskripsi Kegiatan Lainnya" req span={2}>
                <input className="select-input" value={form.kegiatan_lainnya} onChange={set("kegiatan_lainnya")} data-testid="eft-fm-kegiatan-other" />
              </Field>
            )}
            <Field label="Dilakukan Via" req>
              <select className="select-input" value={form.dilakukan_via} onChange={set("dilakukan_via")} data-testid="eft-fm-via">
                <option value="">— Pilih —</option>
                {dropdowns.dilakukan_via.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Bertemu / Berkontak dengan" req>
              <input className="select-input" value={form.bertemu_dengan} onChange={set("bertemu_dengan")} data-testid="eft-fm-bertemu" />
            </Field>
            <Field label="Jabatan" req>
              <select className="select-input" value={form.jabatan} onChange={set("jabatan")} data-testid="eft-fm-jabatan">
                <option value="">— Pilih —</option>
                {dropdowns.jabatan.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
            </Field>
            {form.jabatan === "Lainnya" && (
              <Field label="Tuliskan Jabatan Lainnya" req>
                <input className="select-input" value={form.jabatan_lainnya} onChange={set("jabatan_lainnya")} data-testid="eft-fm-jabatan-other" />
              </Field>
            )}
            <Field label="Produk yang menarik" req>
              <select className="select-input" value={form.produk_menarik} onChange={set("produk_menarik")} data-testid="eft-fm-produk">
                <option value="">— Pilih —</option>
                {dropdowns.produk_menarik.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Melakukan DEMO SpeakEng?" req>
              <select className="select-input" value={form.demo_speakeng} onChange={set("demo_speakeng")} data-testid="eft-fm-demo">
                <option value="">— Pilih —</option>
                {dropdowns.demo_speakeng.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Alasan Tidak Demo / Respon SpeakEng (opsional)" span={2}>
              <input className="select-input" value={form.respon_demo} onChange={set("respon_demo")} data-testid="eft-fm-respon-demo" />
            </Field>
            <Field label="Bagaimana Respon?" req span={2}>
              <input className="select-input" value={form.respon_sekolah} onChange={set("respon_sekolah")} data-testid="eft-fm-respon" />
            </Field>
            <Field label="Detail Issue" req span={2}>
              <textarea className="select-input" rows={3} value={form.detail_issue} onChange={set("detail_issue")} data-testid="eft-fm-issue" />
            </Field>
          </div>
          {err && <div className="alert err" style={{ marginTop: 12 }} data-testid="eft-fm-err"><AlertTriangle size={12} /> {err}</div>}
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-2)", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>Batal</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy} data-testid="eft-fm-submit" style={{ background: "var(--warn)" }}>
            {busy ? <RefreshCw size={14} className="spin" /> : <CheckCircle2 size={14} />}
            {busy ? " Menyimpan…" : " Simpan Aktivitas EFT"}
          </button>
        </div>
      </div>
    </div>
  );
}
